import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  extractNumber,
  extractNumberArr,
  HxReportService,
  HxStoreService,
  HxUiLabelPipe,
  PaymentType,
  ProductInfoType,
  StorageFlowModel,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { DataService } from '@manager-app/service/data.service';
import {
  HxDateRangeSelectComponent,
  HxEnumSelectComponent,
  HxLoaderComponent,
  HxProductCategorySelectComponent,
  HxProductInfoSelectComponent,
  HxStoreSelectComponent
} from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
    selector: 'app-storage-flow.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './storage-flow.component.html',
    styleUrls: ['./storage-flow.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, HxProductInfoSelectComponent, HxEnumSelectComponent, HxProductCategorySelectComponent, NgSelectComponent, NgClass, NgIf, NgFor, HxLoaderComponent, NgbPagination, TranslocoPipe, HxUiLabelPipe]
})
export class StorageFlowComponent implements OnInit, OnDestroy {
  PaymentType = PaymentType;
  storageFlowList: StorageFlowModel[] = [];
  limits = DataService.limits;
  params: {
    storeIds?: number[]
    date?: DateRange,
    productInfoIds?: number[],
    categoryIds?: number[],
    paymentTypes?: PaymentType[],
    page: number,
    limit: number,
  } = {page: 1, limit: 20};
  pagination = {
    allItemCount: 0
  };
  isLoading = {
    list: false
  };
  types = [ProductInfoType.PRODUCT, ProductInfoType.PROPERTY, ProductInfoType.INGREDIENT, ProductInfoType.SERVICE, ProductInfoType.DELIVERY];
  brandId?: number;
  private sub: Subscription;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private storeService: HxStoreService,
    private errorService: ErrorHandlerService,
  ) {
  }

  ngOnInit() {
    this.brandId = localStorage.getItem('defaultBrandId') ? Number(localStorage.getItem('defaultBrandId')) : undefined;
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name) ? paramMap.get(name).split(',').map(mapFn) : undefined;
      this.params.paymentTypes = asArrFn('paymentTypes', str => PaymentType[str]) ?? [];
      this.params.date = extractDateRange('date', paramMap);
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.storeIds = asArrFn('storeIds', id => Number(id)) ?? extractDefaultStoreIds();
      this.params.productInfoIds = extractNumberArr('productInfoIds', paramMap);
      this.params.categoryIds = extractNumberArr('categoryIds', paramMap);
      if (this.params.storeIds.length > 0) {
        this.storeService.getStoreById(this.params.storeIds[0]).then(store => {
          this.brandId = store.brandId;
        });
        this.loadList();
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate();
  }

  onStoreChanged(store: StoreBasicModel | StoreBasicModel[] | undefined) {
    if (Array.isArray(store)) {
      return;
    }
    if (!store || this.brandId !== store?.brandId) {
      this.params.categoryIds = [];
      this.params.productInfoIds = [];
    }
    this.brandId = store?.brandId;
    this.applyFilters();
  }

  applyFilters(): void {
    this.pageChanged(1);
  }

  reset() {
    this.params = {page: 1, limit: 20};
    this.applyFilters();
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadList() {
    this.storageFlowList = [];
    this.isLoading.list = true;
    this.reportService.storageFlowReport(this.params).subscribe(response => {
      this.storageFlowList = response.list;
      this.pagination.allItemCount = response.count;
      this.isLoading.list = false;
    }, error => {
      this.isLoading.list = false;
      this.errorService.handler(error);
    });
  }
}
